import { ButtonProps, Column, Row, Show } from '.'
import { classNameBuilder } from '@/helpers'

type IconButtonColor = 'primary' | 'secondary'
type IconButtonSize = '40' | '80' | 'full'
type IconButtonVariant = 'solid' | 'ghost' | 'outline'
interface IconButtonProps extends Omit<
  ButtonProps,
  'grow' | 'label' | 'size' | 'variant' | 'color'
> {
  color?: 'primary' | 'secondary'
  size?: IconButtonSize
  variant?: IconButtonVariant
}

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  color = 'primary',
  variant = 'solid',
  pending,
  disabled,
  size = '80',
  type,
  onClick
}) => {
  return (
    <button
      tabIndex={pending || disabled ? -1 : undefined}
      type={type}
      disabled={disabled}
      onClick={(event) => {
        if (pending) {
          return
        }
        onClick?.(event)
      }}
      className={[
        'relative shrink-0 rounded-full outline-none focus-visible:ring-4',
        'transition duration-300',
        sizeClassMap[size],
        variantClassMap[variant][color] || '',
        !pending && !disabled ? enabledVariantClassMap[variant][color] : '',
        pending ? pendingClasses : '',
        disabled ? disabledClasses : '',
        disabled ? variantDisabledClassMap[variant][color] || '' : ''
      ].join(' ')}
    >
      <Show when={pending}>
        <Column className="absolute inset-0 items-center justify-center">
          <Spinner
            className={classNameBuilder('text-current', spinnerSizeMap[size])}
          />
        </Column>
      </Show>
      <Row
        className={classNameBuilder(
          'absolute inset-0 items-center justify-center gap-xs',
          pending ? 'invisible' : ''
        )}
      >
        {icon}
      </Row>
    </button>
  )
}

export default IconButton

const sizeClassMap: Record<IconButtonSize, string> = {
  '40': 'w-10 h-10',
  '80': 'w-20 h-20',
  full: 'w-full h-full'
}

const enabledVariantClassMap: Record<
  IconButtonVariant,
  Record<IconButtonColor, string>
> = {
  solid: {
    primary:
      'before:transition before:duration-300 before:opacity-0 before:absolute before:inset-0 before:rounded-full before:bg-black hover:before:opacity-30 active:before:opacity-50 focus:ring-electric-indigo-300',
    secondary:
      'hover:bg-electric-indigo-50 focus:ring-electric-indigo-300 active:bg-electric-indigo-100'
  },
  ghost: {
    primary:
      'hover:bg-electric-indigo-100/30 active:bg-electric-indigo-100/50 focus:ring-electric-indigo-300',
    secondary:
      'hover:bg-electric-indigo-100/30 active:bg-electric-indigo-100/50 focus:ring-electric-indigo-300'
  },
  outline: {
    primary:
      'hover:bg-electric-indigo-100/30 active:bg-electric-indigo-100/50 focus:ring-electric-indigo-300',
    secondary:
      'hover:bg-electric-indigo-100/30 active:bg-electric-indigo-100/50 focus:ring-electric-indigo-300'
  }
}

const variantClassMap: Record<
  IconButtonVariant,
  Record<IconButtonColor, string>
> = {
  solid: {
    primary: 'bg-gradient-to-r from-[#280166] to-[#8B4AF4] text-white',
    secondary: 'bg-white text-electric-indigo-900 shadow'
  },
  ghost: {
    primary: 'text-electric-indigo-900',
    secondary: 'text-white'
  },
  outline: {
    primary:
      'bg-transparent text-electric-indigo-900 border border-electric-indigo-900',
    secondary: 'bg-transparent text-white/50 border border-white/50'
  }
}

const pendingClasses = 'cursor-progress'
const disabledClasses =
  'disabled:text-opacity-50 disabled:border-dashed disabled:shadow-none disabled:cursor-not-allowed'
const variantDisabledClassMap: Record<
  IconButtonVariant,
  Record<IconButtonColor, string>
> = {
  solid: {
    primary: 'disabled:bg-primary-200',
    secondary: 'disabled:bg-secondary-200'
  },
  ghost: {
    primary: 'disabled:bg-primary-50',
    secondary: 'disabled:bg-secondary-50'
  },
  outline: {
    primary:
      'disabled:border-electric-indigo-900/50 text-electric-indigo-900/50',
    secondary: 'disabled:border-white/20 text-white/20'
  }
}

//By Sam Herbert (@sherb), for everyone. More @ http://goo.gl/7AJzbL
function Spinner({ className }: { className: string }) {
  return (
    <svg
      viewBox="0 0 38 38"
      xmlns="http://www.w3.org/2000/svg"
      stroke="currentColor"
      className={className}
    >
      <g fill="none" fillRule="evenodd">
        <g transform="translate(1 1)" strokeWidth="2">
          <circle strokeOpacity=".5" cx="18" cy="18" r="18" />
          <path d="M36 18c0-9.94-8.06-18-18-18">
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 18 18"
              to="360 18 18"
              dur="1s"
              repeatCount="indefinite"
            />
          </path>
        </g>
      </g>
    </svg>
  )
}

const spinnerSizeMap: Record<IconButtonSize, string> = {
  '40': 'w-5 h-5',
  '80': 'w-10 h-10',
  full: 'w-6 h-6'
}
